<template>
  <!-- eslint-disable-next-line vue/no-v-html -->
  <!-- nosemgrep: javascript.vue.security.audit.xss.templates.avoid-v-html.avoid-v-html -->
  <span class="heading-2" v-html="priceHtml"></span>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

const { price } = defineProps<{
  price: string
}>()

// Splits a locale-formatted price string into its display parts.
// Handles all Western formats: $99.00, € 676.00, 676,56 €, CHF 1'234.56, R$ 1.234,56, ¥31,800…
// Returns null only for bare numbers without any currency symbol (e.g. "100").
function splitPriceParts(formatted: string) {
  // With decimals: the decimal separator is always the last [.,]\d{2} in the string.
  const decimalMatch = formatted.match(/^([^0-9]*)(.+?)([.,]\d{2})([^0-9]*)$/)
  if (decimalMatch) {
    return {
      before: decimalMatch[1],
      integer: decimalMatch[2],
      decimal: decimalMatch[3] as string | null,
      after: decimalMatch[4],
    }
  }

  // No decimals: still split off any currency prefix/suffix (e.g. ¥31,800).
  const currencyMatch = formatted.match(
    /^([^0-9]*)(\d[\d\s,.']*\d|\d)([^0-9]*)$/,
  )
  if (!currencyMatch || (!currencyMatch[1] && !currencyMatch[3])) return null

  return {
    before: currencyMatch[1],
    integer: currencyMatch[2],
    decimal: null as string | null,
    after: currencyMatch[3],
  }
}

const SMALL_SPAN_CLASSES = 'caption-bold align-text-top inline-block mt-2'

function smallSpan(content: string) {
  return `<span class="${SMALL_SPAN_CLASSES}">${content}</span>`
}

// Safe to use v-html: content is produced by Intl.NumberFormat or API-formatted price strings
// (digits, currency symbols, punctuation) — no user-controlled input.
const priceHtml = computed(() => {
  const parts = splitPriceParts(price)
  if (!parts) return price

  const { before, integer, decimal, after } = parts

  return [
    before ? smallSpan(before.trimEnd()) : '',
    integer,
    decimal ? smallSpan(decimal) : '',
    after ? smallSpan(after) : '',
  ].join('')
})
</script>
